import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import { Alert, AlertDescription, AlertIcon, Button } from '@chakra-ui/react'
import { useContext, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import { ActiveEvents, EndpointField } from './WebhookModalCommon'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import type { Webhook } from '../../interfaces/WebhooksGetResponse'
import { UserContext } from '../../providers'

interface Props {
  isOpen: boolean
  existingWebhookEndpoints: string[]
  onClose(): void
  reloadWebhooks(): void
  onWebhookCreate(webhook: Webhook): void
}

const WebhookCreateModal = ({
  isOpen,
  existingWebhookEndpoints,
  onClose,
  reloadWebhooks,
  onWebhookCreate,
}: Props): React.ReactElement => {
  const { request } = useFetch<{ webhook?: Webhook; errorReason?: string }>({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const toast = useToast()
  const { user } = useContext(UserContext)
  const { t } = useTranslation()
  const [formError, setFormError] = useState<string>('')

  const [endpoint, setEndpoint] = useState('')
  const [activeEvents, setActiveEvents] = useState([])

  useEffect(() => {
    if (isOpen) {
      setEndpoint('')
      setActiveEvents([])
    }
  }, [isOpen])

  const closeModal = (): void => {
    setFormError('')
    onClose()
  }

  const handleSubmit = async () => {
    const clientNumber = user?.clientNumber
    if (!clientNumber) {
      return
    }

    await request
      .post(`/api/clients/${clientNumber}/settings/developer/webhook`, {
        endpoint: endpoint,
        events: activeEvents,
      })
      .then(response => {
        if (response.webhook) {
          onWebhookCreate(response.webhook)
          toast({
            description: t('webhook_created', 'Webhook created'),
            status: 'success',
            duration: 5000,
            isClosable: true,
          })
          reloadWebhooks()
          closeModal()
        } else {
          switch (response.errorReason) {
            case 'MAX_WEBHOOK_LIMIT_REACHED':
              setFormError(
                t(
                  'max_webhooks_limit_reached',
                  'Maximum webhooks limit reached',
                ),
              )
              reloadWebhooks() // This situation can happen if the webhook endpoint was by another user.
              break
            case 'WEBHOOK_ALREADY_EXISTS':
              setFormError(
                t(
                  'webhook_endpoint_already_exists',
                  'Webhook endpoint already exists',
                ),
              )
              reloadWebhooks() // This situation can happen if the webhook endpoint was by another user.
              break
            default:
              setFormError(
                request.error?.message ?? t('unknown_error', 'Unknown error'),
              )
          }
        }
      })
      .catch(error => {
        setFormError(error?.message)
      })
  }

  return (
    <Modal isOpen={isOpen} onClose={closeModal}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('create_new_webhook', 'Create new webhook')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody spacing={10}>
          {formError ? (
            <Alert status="error" mb="1em" minWidth="unset">
              <AlertIcon />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          ) : null}
          <EndpointField
            existingWebhookEndpoints={existingWebhookEndpoints}
            endpoint={endpoint}
            setEndpoint={setEndpoint}
          />
          <ActiveEvents
            activeEvents={activeEvents}
            setActiveEvents={setActiveEvents}
          />
        </ModalBody>
        <ModalFooter
          borderTop={`1px solid ${colors.appGrey}`}
          style={{ gap: '1em' }}
        >
          <Button onClick={closeModal} variant="outline">
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            isDisabled={endpoint === '' || activeEvents.length === 0}
          >
            {t('create', 'Create')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default WebhookCreateModal
